import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import U2000BasicDialog from '../../../u2000Core/components/dialog/U2000BasicDialog';
import type { U3121UploadFileLineResult } from '../../tools/U3121UploadProjectFromFileBatchTools';
import { NS } from '../../U3121';

export interface U3121UploadProjectFromFileRecapDialogProps {
  isOpen: boolean;
  results: U3121UploadFileLineResult[];
  onClose: () => void;
}

export const U3121UploadProjectFromFileRecapDialog: React.FC<U3121UploadProjectFromFileRecapDialogProps> = ({
  isOpen,
  results,
  onClose,
}) => {
  const { t } = useTranslation();

  const { successCount, errorCount } = useMemo(() => {
    let success = 0;
    let error = 0;
    results.forEach((line) => {
      if (line.status === 'success') success += 1;
      else error += 1;
    });
    return { successCount: success, errorCount: error };
  }, [results]);

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t('dlgUploadProjectFromFileRecapTitle', { ns: NS })}
      width="medium"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          <p className="mb-4 text-sm text-gray-700">
            {t('msgUploadBatchRecapSummary', { ns: NS, successCount, errorCount })}
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                    {t('lblUploadRecapColumnFile', { ns: NS })}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                    {t('lblUploadRecapColumnResult', { ns: NS })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {results.map((line) => (
                  <tr key={`${line.fileName}-${line.status}-${line.projectId ?? line.errorMessage}`}>
                    <td className="px-4 py-2 text-gray-900 break-all">{line.fileName}</td>
                    <td className="px-4 py-2">
                      {line.status === 'success' && line.projectId != null ? (
                        <Link
                          to={`/U3121/${line.projectId}`}
                          className="text-blue-600 hover:underline"
                          onClick={() => onClose()}
                        >
                          {line.projetNumber
                            ? t('msgUploadRecapSuccessWithNumber', { ns: NS, number: line.projetNumber })
                            : t('msgUploadRecapSuccessOpenProject', { ns: NS })}
                        </Link>
                      ) : (
                        <span className="text-red-700">{line.errorMessage ?? t('errorTitle', { ns: NS })}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <footer className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl shrink-0">
          <button
            type="button"
            onClick={() => onClose()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs hover:bg-gray-50"
          >
            {t('btnClose', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
